/* eslint-disable no-param-reassign */
/* eslint-disable eqeqeq */
import baseFormValidators from '@/composables/validations/baseFormValidators';
import { i18n } from '@/packages/i18n';
import useFileInputValidations from '@/composables/validations/useFileInputValidations';
import { useOccupationStore } from '@/stores/forms/occupationFormStore';
import walletAddressValidator from '@/composables/validations/walletAddressValidator';

const {
  isEmpty,
  minLength,
  isEmail,
  getMobileErrorMsg,
  getMobileCustomErrorMsg,
} = baseFormValidators();
const {
  isFileTypeAllowed,
  isSingleFileSizeInLimits,
  areMultiFileSizesInLimits,
  areMultiFileTypesAllowed,
} = useFileInputValidations();
const {
  validateWalletByBlockchains,
  validateWalletBySelectedBlockchain,
} = walletAddressValidator();

export default function useFormValidation() {
  const validateNameField = (fieldName, fieldValue) => {
    if (!fieldValue) {
      return '';
    }

    return minLength(fieldValue, 1);
  };

  const validateSurnameField = (fieldName, fieldValue) => {
    if (!fieldValue) {
      return;
    }

    // eslint-disable-next-line consistent-return
    return minLength(fieldValue, 1);
  };

  const validateEmailField = (fieldName, fieldValue) => {
    if (!fieldValue) {
      return '';
    }

    return isEmpty(fieldName, fieldValue) || isEmail(fieldName, fieldValue);
  };

  const validateMobileField = (mobile, countryCode) => {
    if (!mobile) {
      return '';
    }

    return getMobileErrorMsg(mobile, countryCode) === ''
      ? getMobileCustomErrorMsg(mobile, countryCode)
      : getMobileErrorMsg(mobile, countryCode);
  };

  const validateOccupationField = () => {
    const occupationStore = useOccupationStore();
    return occupationStore.getErrorByField('occupationOther');
  };

  const validateCheckboxField = (fieldName, fieldValue) => {
    if (!fieldValue || fieldValue == false) {
      return '';
    }
    return isEmpty(fieldName, fieldValue);
  };

  const validateCountryField = (fieldName, country, countryList) => (!countryList.includes(country)
    ? `The input is not a valid ${fieldName}`
    : '');

  const validateVerificationCodeField = (field, pincodeValue) => {
    const { t } = i18n.global;

    if (!pincodeValue) {
      return '';
    }
    const pincodeLength = import.meta.env.VITE_APP_PINCODE_LENGTH;

    return pincodeValue.length != pincodeLength
      ? t('VerificationCodeInputField.error.verificationCodeErrorMsg')
      : '';
  };

  const validateWalletAddressField = async (walletAddress, blockchain) => {
    const { t } = i18n.global;

    if (!walletAddress || !blockchain || blockchain.length === 0) {
      return '';
    }

    if (blockchain.includes('No valid blockchains available')) {
      return t('FormInput.error.validateWalletAddressErrorMsg');
    }

    const validatedWalletAddress = await validateWalletBySelectedBlockchain(
      walletAddress,
      blockchain,
    );

    return Object.values(validatedWalletAddress).includes(false)
      ? t('FormInput.error.validateWalletAddressErrorMsg')
      : '';
  };
  const validateFiatField = (fiatAmount, fiatType, transactionalLimit) => {
    const { t } = i18n.global;

    if (!fiatAmount || !transactionalLimit) {
      return '';
    }
    const { min, max } = transactionalLimit;

    return parseInt(fiatAmount, 10) > parseInt(max, 10)
      || parseInt(fiatAmount, 10) < parseInt(min, 10)
      ? t('FormInput.error.validateFiatAmountErrorMsg', {
        fiatType,
        min,
        max,
      })
      : '';
  };

  const validateCoinField = (coinAmount, coinType, blockchain, coinData) => {
    const { t } = i18n.global;

    if (!coinAmount || !coinData || !coinType) {
      return '';
    }

    const activeCoinData = coinData.find(
      (coin) => coinType === coin?.coin_code,
    );

    const minValueByCoin = activeCoinData?.min_value;
    const blockchainDataByCoin = activeCoinData.blockchains.find(
      (coin) => blockchain.value === coin.code,
    );
    const minValueByBlockchainCoin = blockchainDataByCoin?.min_value;
    const minCoin = minValueByBlockchainCoin || minValueByCoin;

    return coinAmount < minCoin
      ? t('FormInput.error.validateCoinAmountErrorMsg', { minCoin })
      : '';
  };

  const validateBlockchainField = async (walletAddress, blockchains) => {
    const { t } = i18n.global;

    if (!walletAddress || !blockchains || blockchains.length === 0) {
      return '';
    }

    const validatedWalletAddress = await validateWalletByBlockchains(
      walletAddress,
      blockchains,
    );
    const walletsAreFalse = (wallet) => wallet === false;

    return Object.values(validatedWalletAddress).every(walletsAreFalse)
      ? t('FormInput.error.blockchainValidationErrorMsg')
      : '';
  };

  const validateFileInput = (file) => (
    isFileTypeAllowed(file) || isSingleFileSizeInLimits(file)
  );

  const validateMultiFileInput = (fieldName, files) => (
    areMultiFileSizesInLimits(files) || areMultiFileTypesAllowed(files)
  );

  // https://stackoverflow.com/a/7091965
  function getAgeInYears(dateString) {
    const today = new Date();
    const birthDate = new Date(dateString);

    // Set both dates to midnight UTC to avoid timezone issues
    today.setUTCHours(0, 0, 0, 0);
    birthDate.setUTCHours(0, 0, 0, 0);

    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    // If birthday hasn't occurred yet this year, subtract one year
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }

    return age;
  }

  const validateDobField = (fieldName, dob) => {
    if (!dob) return '';

    const { t } = i18n.global;

        const dobFormat = fieldName === 'dob' ? 'DD-MM-YYYY' : 'YYYY-MM-DD';

    if (!dob.includes('-') || dob.split('-').length !== 3) {
      return `${t('FormInput.error.dobValidationErrorMsg')} ${dobFormat}`;
    }

      let year;
      let month;
      let day;

    // Check if the date is in YYYY-MM-DD or DD-MM-YYYY format
    const ymdMatch = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const dmyMatch = dob.match(/^(\d{2})-(\d{2})-(\d{4})$/);

    // Find year month and day from the date string
    if (ymdMatch) {
      [year, month, day] = ymdMatch.slice(1);
    } else if (dmyMatch) {
      [day, month, year] = dmyMatch.slice(1);
    } else {
      return `${t('FormInput.error.dobValidationErrorMsg')} ${dobFormat}`;
    }

    if (!day || !month || !year || year.length !== 4) {
      return `${t('FormInput.error.dobValidationErrorMsg')} ${dobFormat}`;
    }

    const dd = parseInt(day, 10);
    const mm = parseInt(month, 10) - 1; // Months are 0-based (_Thanks Java_)
    const yyyy = parseInt(year, 10);
    const date = new Date(yyyy, mm, dd);

    if (date.getFullYear() !== yyyy || date.getMonth() !== mm || date.getDate() !== dd) {
      return `${t('FormInput.error.dobValidationErrorMsg')} ${dobFormat}`;
    }

    const isoDate = date.toISOString().slice(0, 10); // "YYYY-MM-DD"
    const usersAge = getAgeInYears(isoDate);
    const maxAllowedAge = Number(import.meta.env.VITE_APP_HIGHEST_ALLOWED_DOB);
    const minAllowedAge = Number(import.meta.env.VITE_APP_LOWEST_ALLOWED_DOB);

    if (usersAge < minAllowedAge || usersAge > maxAllowedAge) {
      return t('FormInput.error.ageValidationErrorMsg');
    }

    return '';
  };

  const validateSSNField = (field, ssn) => {
    const { t } = i18n.global;

    if (!ssn) return '';
    return /^(?!(000|666|9))\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/.test(ssn)
      ? ''
      : t('FormInput.error.ssnValidationErrorMsg');
  };

  const validateBSBField = (field, bsb) => {
    const { t } = i18n.global;

    if (!bsb) return '';
    return bsb.length === 7 && /^[0-9]{3}-[0-9]{3}$/.test(bsb)
      ? ''
      : t('FormInputField.error.bsbErrorMsg');
  };

  const validateAccountNumberField = (field, accountNo) => {
    const { t } = i18n.global;

    if (!accountNo) return '';
    return accountNo.length <= 9
      && accountNo.length >= 5
      && /^[0-9]+$/.test(accountNo)
      ? ''
      : t('FormInputField.error.accountNumberFieldErrorMsg');
  };

  const validateIBANField = (field, iban) => {
    const { t } = i18n.global;

    if (!iban) return '';
    return iban.length <= 34 && iban.length >= 5 && /^[A-Za-z0-9 ]*$/.test(iban)
      ? ''
      : t('FormInput.error.ibanValidationErrorMsg');
  };

  const validateWalletAddressTag = (walletTag, walletTagData) => {
    const { t } = i18n.global;

    if (!walletTag || !walletTagData) {
      return '';
    }

    const { name, regex } = walletTagData;
    const coinRegex = new RegExp(regex);
    return `${walletTag}`.search(coinRegex) === -1
      ? t('FormInput.error.walletAddressTagValidationErrorMsg', {
        name: name.toLowerCase(),
      })
      : '';
  };

  const validateCurpField = (field, curp) => {
    const { t } = i18n.global;

    if (!curp) {
      return '';
    }
    return /^[a-zA-Z]{4}\d{6}[a-zA-Z]{6}[a-zA-Z0-9]{2}$/.test(curp)
      ? ''
      : t('FormInput.error.curpValidationErrorMsg');
  };

  const validateCpfField = (field, cpf) => {
    const { t } = i18n.global;

    if (!cpf) {
      return '';
    }
    return /^\d{11}$/.test(cpf)
      ? ''
      : t('FormInput.error.cpfValidationErrorMsg');
  };

  const validateCnpjField = (field, cnpf) => {
    const { t } = i18n.global;

    if (!cnpf) {
      return '';
    }
    return /^\d{3}(\.\d{3}){2}-\d{2}$|^\d{11}$|^\d{2}(\.\d{3}){2}\/\d{4}-\d{2}$|^\d{14}$/.test(cnpf)
      ? ''
      : t('FormInput.error.pixDocValidationErrorMsg');
  };

  const validateSaidField = (field, said) => {
    const { t } = i18n.global;

    if (!said) {
      return '';
    }
    return /^\d{13}$/.test(said)
      ? ''
      : t('FormInput.error.saidValidationErrorMsg');
  };

  const validateSortCodeField = (field, sortCode) => {
    const { t } = i18n.global;

    if (!sortCode) return '';
    return sortCode.length === 8
      && /^[0-9]{2}-[0-9]{2}-[0-9]{2}$/.test(sortCode)
      ? ''
      : t('ActionModal.title.sortFieldErrorMsg');
  };

  const validateCcField = (field, cc) => {
    const { t } = i18n.global;

    if (!cc) {
      return '';
    }
    return /^\d{6,10}$/.test(cc)
      ? ''
      : t('FormInput.error.ccValidationErrorMsg');
  };

  const validateDniField = (field, dni) => {
    const { t } = i18n.global;

    if (!dni) {
      return '';
    }
    return /^\d{7,9}$|^\d{11}$/.test(dni)
      ? ''
      : t('FormInput.error.dniValidationErrorMsg');
  };

  const validateCiField = (field, ci) => {
    const { t } = i18n.global;

    if (!ci) {
      return '';
    }
    return /^\d{7,8}([\dkK]){1}$/.test(ci)
      ? ''
      : t('FormInput.error.ciValidationErrorMsg');
  };

  const validateTicField = (field, tic) => {
    const { t } = i18n.global;

    if (!tic) {
      return '';
    }
    return /^\d{13}$/.test(tic)
      ? ''
      : t('FormInput.error.ticValidationErrorMsg');
  };

  const validateAddressLine1Field = (field, address) => {
    const { t } = i18n.global;

    if (!address) return '';
    return /^(?!.*(?:(.*((p|post)[-.\s]*(o|off|office)[-.\s]*(box|bin)[-.\s]*)|.*((p |post)[-.\s]*(box|bin)[-.\s]*)))).*$/i.test(
      address,
    )
      ? ''
      : t('FormInput.error.addressLine1ValidationErrorMsg');
  };

  const validateAddress = (address, getUSState, isUnitedStates, countries, store) => {
    const isFieldValid = (field) => field && field.trim().length > 0;
    const { t } = i18n.global;

    const subPremise = address?.subpremise?.long_name;
    const streetNumber = address?.street_number?.long_name;
    const suburb = address?.locality?.long_name
    || address?.sublocality_level_1?.long_name // for US addresses
    || address?.postal_town?.long_name; // for UK addresses;
    const streetName = address?.route?.long_name;
    const state = address?.administrative_area_level_1?.short_name;
    const postcode = address?.postal_code?.long_name;
    const country = address?.country?.long_name;

    const isCountryValid = country && countries.some(
      (supportedCountry) => supportedCountry.name.toLowerCase() === country.toLowerCase(),
    );

    if (country && !isCountryValid) {
      store.form.country.error = t('FormSelect.error.countryErrorMsg', { country });
    }

    const isUSStateValid = isUnitedStates && state && getUSState(state);
    const requiredFields = [streetNumber, streetName, suburb, state, postcode, country];
    const validations = requiredFields.map((field) => isFieldValid(field));

    validations.push(isCountryValid, !isUnitedStates || isUSStateValid);

    if (!validations.every(Boolean)) {
      return true;
    }

    // Optional fields validation (ie. subPremise)
    if (subPremise && !isFieldValid(subPremise)) {
      return true;
    }

    return false;
  };
  const validateAutoComplete = (
    address,
    isUnitedStates,
    getUSState,
    store,
    countries,
  ) => {
    const { t } = i18n.global;

    const isNotAValidAddress = validateAddress(
      address,
      getUSState,
      isUnitedStates.value,
      countries,
      store,
    );

    if (isNotAValidAddress) {
      return t('FormInput.error.addressValidationErrorMsg');
    }
    return '';
  };

  const validateBrazilPhoneNumber = (field, phone) => {
    const { t } = i18n.global;

    if (!phone) {
      return '';
    }
    return /^\+(55)\d{11}$/.test(phone)
      ? ''
      : t('FormInput.error.phoneValidationErrorMsg');
  };

  const validateBrazilBankAccount = (field, bankAccount) => {
    const { t } = i18n.global;

    if (!bankAccount) {
      return '';
    }
    return /^[A-Za-z0-9]{8}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{12}$/.test(bankAccount)
      ? ''
      : t('FormInput.error.bankAccountValidationErrorMsg');
  };

  const validateBICField = (field, bic) => {
    const { t } = i18n.global;

    if (!bic) return '';
    return /^[a-zA-Z\d]{8,11}$/.test(bic)
      ? ''
      : t('FormInput.error.bicValidationErrorMsg');
  };

  const validateTextField = (field, value) => {
    const { t } = i18n.global;

    if (!value) return '';
    return /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9\s]*$/.test(value)
      ? ''
      : t('FormInput.error.textValidationErrorMsg');
  };

  return {
    validateNameField,
    validateSurnameField,
    validateEmailField,
    validateCountryField,
    validateCheckboxField,
    validateVerificationCodeField,
    validateWalletAddressField,
    validateBlockchainField,
    validateFileInput,
    validateMultiFileInput,
    validateMobileField,
    validateFiatField,
    validateCoinField,
    validateDobField,
    validateCurpField,
    validateCpfField,
    validateSaidField,
    validateSSNField,
    validateOccupationField,
    validateBSBField,
    validateAccountNumberField,
    validateIBANField,
    validateWalletAddressTag,
    validateSortCodeField,
    validateCcField,
    validateDniField,
    validateCiField,
    validateTicField,
    validateAddressLine1Field,
    validateAutoComplete,
    validateBrazilPhoneNumber,
    validateCnpjField,
    validateBrazilBankAccount,
    validateBICField,
    validateTextField,
  };
}
